import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  ShieldAlert,
  Plus,
  Trash2,
  Copy,
  Mail,
  Save,
  RotateCcw,
  FileDown,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  buildReport,
  reviewChecklist,
  calcDuration,
  sumActivities,
  QUICK_PHRASES,
  polishProse,
} from "@/lib/ddr/templates";
import { saveDdrDraft } from "@/lib/ddr/storage";
import { FeatureLockedError } from "@/lib/featureAccess";
import { exportDdrPdf } from "@/lib/ddr/pdfExport";
import PolishPanel from "@/components/PolishPanel";
import FloatingActionToolbar from "@/components/FloatingActionToolbar";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import { resolve } from "@/lib/rce/resolver";
import {
  createDailyOpsDocument, getDailyOpsDocument,
  updateDailyOpsDocument, rePullContextSnapshot,
} from "@/lib/dailyOps/storage";
import { CONTEXT_VERSION, DAILY_OPS_KINDS } from "@/lib/dailyOps/kinds";

// ============================================================================
// Daily Drilling Report — accordion form + live polished preview.
// ============================================================================

const OP_TYPES = [
  "Drilling",
  "Tripping in hole",
  "Tripping out of hole",
  "Circulating",
  "Reaming",
  "Backreaming",
  "Conditioning mud",
  "Running casing",
  "Cementing",
  "Logging",
  "Testing BOP",
  "Waiting on weather",
  "Waiting on equipment",
  "Repair",
  "Standby",
  "Rig move",
  "Completion",
  "Workover",
  "Other",
];

const emptyState = () => ({
  id: null,
  header: { unitSystem: "imperial" },
  well: {},
  ops: {},
  activities: [],
  params: {},
  mud: {},
  bha: {},
  equipment: {},
  bop: {},
  hse: {},
  pob: {},
  logistics: {},
  weather: {},
  npt: {},
});

// Small primitives ----------------------------------------------------------
function Field({ label, helper, children, testId }) {
  return (
    <label className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-[11px] font-semibold text-[var(--rs-text-2)] tracking-wide">
        {label}
      </span>
      {children}
      {helper && (
        <span className="text-[10px] text-[var(--rs-text-3)] leading-snug">{helper}</span>
      )}
    </label>
  );
}

function TextInput({ value, onChange, type = "text", placeholder, testId }) {
  // RC-1 Data Integrity Fix — blur on wheel for numeric inputs so the browser
  // can't silently mutate engineering values while the operator scrolls.
  const guardedOnWheel = type === "number"
    ? (e) => { if (e.currentTarget === document.activeElement) e.currentTarget.blur(); }
    : undefined;
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onWheel={guardedOnWheel}
      placeholder={placeholder}
      data-testid={testId}
      className="input-rs"
    />
  );
}

function TextArea({ value, onChange, rows = 4, placeholder, testId }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      data-testid={testId}
      className="input-rs"
      style={{ minHeight: 72, resize: "vertical" }}
    />
  );
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="input-rs"
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function Checkbox({ checked, onChange, label, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-[12px] text-[var(--rs-text-2)]" data-testid={testId}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Section({ id, title, badge, open, onToggle, children }) {
  return (
    <section
      data-testid={`ddr-section-${id}`}
      className="card-rs"
      style={{ padding: 0, overflow: "hidden" }}
    >
      <button
        type="button"
        data-testid={`ddr-section-toggle-${id}`}
        onClick={onToggle}
        className="w-full px-3.5 py-3 flex items-center justify-between text-left hover:bg-[rgba(255,179,0,0.04)] transition-colors"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="font-mono-rs text-[9px] tracking-[0.18em] uppercase px-1.5 py-[1px] rounded-[3px] font-bold"
            style={{
              background: "rgba(255,179,0,0.14)",
              color: "var(--rs-amber)",
              border: "1px solid rgba(255,179,0,0.4)",
            }}
          >
            {badge}
          </span>
          <span className="font-heading font-black text-sm tracking-tight truncate">{title}</span>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-3.5 pb-4 pt-1 border-t border-[var(--rs-border)] flex flex-col gap-3">
          {children}
        </div>
      )}
    </section>
  );
}

function QuickPhrases({ onPick, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {QUICK_PHRASES.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPick(p)}
          className="text-[10.5px] px-2 py-1 rounded-md transition-colors"
          style={{
            background: "rgba(22,24,29,0.6)",
            color: "var(--rs-text-2)",
            border: "1px solid var(--rs-border)",
          }}
        >
          + {p}
        </button>
      ))}
    </div>
  );
}

// Main page -----------------------------------------------------------------
export default function DailyDrillingReport() {
  const navigate = useNavigate();
  const [state, setState] = useState(emptyState());
  const [open, setOpen] = useState({ header: true, well: false, ops: false, activities: false, params: false, mud: false, bha: false, equipment: false, bop: false, hse: false, pob: false, logistics: false, weather: false, npt: false });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  // RCE-2 — Daily Operations integration -----------------------------------
  const { session } = useEngineeringSession();
  // Stable RCE context derived from the active Engineering Session. Used by
  // every `resolve(...)` call below and persisted on first save.
  const rceCtx = useMemo(() => ({
    projectId:     session?.projectId     || "",
    rigId:         session?.rigId         || "",
    wellId:        session?.wellId        || "",
    bhaId:         session?.bhaId         || "",
    drillStringId: session?.drillStringId || "",
  }), [session]);

  // Track the persisted DailyOps document id (parallel to the legacy DDR
  // draft id). `null` until the user saves for the first time.
  const [opsDocId, setOpsDocId] = useState(null);
  const [opsDocSnapshot, setOpsDocSnapshot] = useState(null);

  // On first mount, populate empty identity / context fields from the
  // resolver if the user already has an active Engineering Session. This
  // never overwrites user input — only blanks.
  useEffect(() => {
    if (!session) return;
    setState((s) => {
      const next = { ...s, header: { ...s.header }, well: { ...s.well } };
      const inh = (path) => {
        const r = resolve(path, rceCtx);
        return (r.confidence !== "unknown" && r.confidence !== "conflict") ? r.value : "";
      };
      const fill = (group, key, value) => {
        if (!value) return;
        if (next[group][key] != null && String(next[group][key]).trim() !== "") return;
        next[group][key] = value;
      };
      fill("header", "rigName",    inh("rig.name"));
      fill("header", "contractor", inh("rig.contractor"));
      fill("header", "operator",   inh("project.operator"));
      fill("header", "wellName",   inh("well.name"));
      fill("header", "field",      inh("project.field"));
      fill("header", "country",    inh("project.country"));
      fill("well",   "holeSection",inh("well.holeSection"));
      fill("well",   "holeSize",   String(inh("well.holeSizeIn") || ""));
      fill("well",   "currentMd",  String(inh("well.currentMdFt")  || ""));
      fill("well",   "currentTvd", String(inh("well.currentTvdFt") || ""));
      fill("well",   "shoeMd",     String(inh("well.casingShoeMdFt") || ""));
      fill("well",   "shoeTvd",    String(inh("well.casingShoeTvdFt") || ""));
      fill("well",   "waterDepth", String(inh("well.waterDepthFt") || ""));
      return next;
    });
  }, [session?.projectId, session?.rigId, session?.wellId, session?.bhaId, session?.drillStringId]);

  const update = (group, key, value) =>
    setState((s) => ({ ...s, [group]: { ...s[group], [key]: value } }));

  // Activities -------------------------------------------------------------
  const addActivity = () =>
    setState((s) => ({
      ...s,
      activities: [
        ...s.activities,
        { start: "", end: "", duration: "", activityType: "", depthFrom: "", depthTo: "", notes: "", npt: false, responsible: "", comments: "" },
      ],
    }));
  const deleteActivity = (idx) =>
    setState((s) => ({ ...s, activities: s.activities.filter((_, i) => i !== idx) }));
  const updateActivity = (idx, key, value) =>
    setState((s) => ({
      ...s,
      activities: s.activities.map((a, i) => {
        if (i !== idx) return a;
        const next = { ...a, [key]: value };
        if (key === "start" || key === "end") {
          const d = calcDuration(next.start, next.end);
          if (d !== null) next.duration = d;
        }
        return next;
      }),
    }));

  const sums = useMemo(() => sumActivities(state.activities), [state.activities]);
  const issues = useMemo(() => reviewChecklist(state), [state]);
  const polished = useMemo(() => buildReport(state), [state]);

  // Actions ----------------------------------------------------------------
  const onSave = () => {
    // Save the legacy DDR draft (existing storage) so nothing breaks.
    let item;
    try {
      item = saveDdrDraft(state);
    } catch (e) {
      if (e instanceof FeatureLockedError) {
        toast.error(e.message, {
          duration: 5000,
          action: { label: "Upgrade", onClick: () => navigate("/pricing") },
        });
        return;
      }
      throw e;
    }
    setState((s) => ({ ...s, id: item.id }));
    // RCE-2 — also persist as a Daily Ops document with frozen context
    // snapshot. On subsequent saves the snapshot is preserved (audit-trail
    // invariant); only payload + status get patched.
    try {
      if (!opsDocId) {
        const doc = createDailyOpsDocument("ddr", {
          header:     state.header,
          well:       state.well,
          ops:        state.ops,
          activities: state.activities,
          params:     state.params,
          mud:        state.mud,
          bha:        state.bha,
          equipment:  state.equipment,
          bop:        state.bop,
          hse:        state.hse,
          pob:        state.pob,
          logistics:  state.logistics,
          weather:    state.weather,
          npt:        state.npt,
        }, rceCtx);
        setOpsDocId(doc.id);
        setOpsDocSnapshot(doc.contextSnapshot);
      } else {
        const doc = updateDailyOpsDocument(opsDocId, {
          payload: {
            header: state.header, well: state.well, ops: state.ops,
            activities: state.activities, params: state.params, mud: state.mud,
            bha: state.bha, equipment: state.equipment, bop: state.bop,
            hse: state.hse, pob: state.pob, logistics: state.logistics,
            weather: state.weather, npt: state.npt,
          },
        });
        setOpsDocSnapshot(doc.contextSnapshot);
      }
    } catch (err) {
      // Daily Ops persistence is best-effort in RCE-2 — never block the
      // legacy DDR save flow on it.
      console.warn("RCE Daily Ops persist failed:", err);
    }
    toast.success("Draft saved locally");
  };

  const onRePull = () => {
    if (!opsDocId) {
      toast.error("Save the draft first before re-pulling from context.");
      return;
    }
    try {
      const refreshed = rePullContextSnapshot(opsDocId, rceCtx);
      setOpsDocSnapshot(refreshed.contextSnapshot);
      // Refresh the editable header/well fields from the new snapshot. This
      // OVERWRITES the corresponding fields — user opted in by tapping
      // Re-pull, which is documented as the explicit refresh action.
      setState((s) => {
        const cs = refreshed.contextSnapshot;
        return {
          ...s,
          header: { ...s.header,
            rigName:    cs.rig.name        || s.header.rigName,
            contractor: cs.rig.contractor  || s.header.contractor,
            operator:   cs.project.operator|| s.header.operator,
            wellName:   cs.well.name       || s.header.wellName,
            field:      cs.project.field   || s.header.field,
            country:    cs.project.country || s.header.country,
          },
          well: { ...s.well,
            holeSection: cs.well.holeSection    || s.well.holeSection,
            holeSize:    String(cs.well.holeSizeIn    || s.well.holeSize    || ""),
            currentMd:   String(cs.well.currentMdFt   || s.well.currentMd   || ""),
            currentTvd:  String(cs.well.currentTvdFt  || s.well.currentTvd  || ""),
            shoeMd:      String(cs.well.casingShoeMdFt|| s.well.shoeMd      || ""),
            shoeTvd:     String(cs.well.casingShoeTvdFt|| s.well.shoeTvd    || ""),
            waterDepth:  String(cs.well.waterDepthFt  || s.well.waterDepth  || ""),
          },
        };
      });
      toast.success("Re-pulled engineering context from RCE");
    } catch (e) {
      toast.error(e.message || "Re-pull failed");
    }
  };

  const onReset = () => {
    if (window.confirm("Reset the entire form? Unsaved input will be lost.")) {
      setState(emptyState());
      toast("Form reset");
    }
  };
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(polished);
      toast.success("Report copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };
  const onEmail = () => {
    const subject = `Daily Drilling Report${state.header?.reportNumber ? ` #${state.header.reportNumber}` : ""}`;
    const body = polished.slice(0, 1800); // mailto URI cap
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const onPdf = () => {
    try {
      exportDdrPdf(state);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  // Append a quick phrase to a target rough-notes textarea.
  const appendPhrase = (group, key, phrase) => {
    setState((s) => {
      const prev = (s[group] && s[group][key]) || "";
      const sep = prev && !/\s$/.test(prev) ? " " : "";
      return { ...s, [group]: { ...s[group], [key]: prev + sep + phrase } };
    });
  };

  // Polish preview helper for rough-note fields.
  const previewPolished = (text) => polishProse(text) || "";

  return (
    <div className="flex flex-col gap-4 bg-grain pb-2" data-testid="ddr-page">
      <section className="pt-1">
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">
          Documentation · DDR
        </div>
        <h1 className="font-heading font-black text-3xl tracking-tight mt-1" data-testid="ddr-title">
          Daily Drilling Report
        </h1>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed">
          Write what happened in simple words. RigSentinel cleans the wording into a professional report. The tool will mark missing fields as <em>Not entered</em> rather than inventing data.
        </p>
      </section>

      {/* Top disclaimer */}
      <div
        data-testid="ddr-disclaimer"
        className="card-rs p-3 flex items-start gap-2.5"
        style={{
          background: "rgba(22,24,29,0.65)",
          borderColor: "var(--rs-border)",
          borderLeft: "2px solid var(--rs-amber)",
        }}
      >
        <ShieldAlert className="w-3.5 h-3.5 mt-[3px] shrink-0" style={{ color: "var(--rs-amber)" }} />
        <p className="text-[11px] leading-snug text-[var(--rs-text-3)]">
          RigSentinel Daily Drilling Report tools provide draft documentation support only. All reports must be reviewed, corrected, and approved by competent rig/site personnel under company procedures, client requirements, and applicable regulations before submission.
        </p>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2" data-testid="ddr-actions">
        <button data-testid="ddr-save-btn" onClick={onSave} className="btn-amber h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> Save Draft
        </button>
        <button
          data-testid="ddr-repull-btn"
          onClick={onRePull}
          disabled={!opsDocId}
          className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5 disabled:opacity-40"
          title={opsDocId ? "Refresh frozen engineering context from the live RCE" : "Save the draft first to enable re-pull"}
        >
          <RotateCcw className="w-3.5 h-3.5" /> Re-pull from Context
        </button>
        <button data-testid="ddr-pdf-btn" onClick={onPdf} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <FileDown className="w-3.5 h-3.5" /> Export PDF
        </button>
        <button data-testid="ddr-copy-btn" onClick={onCopy} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
        <button data-testid="ddr-email-btn" onClick={onEmail} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <button data-testid="ddr-reset-btn" onClick={onReset} className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </button>
      </div>

      {/* Context Snapshot strip — only when a session is active or a doc
          has been saved. Shows the frozen engineering context the report
          was created against. */}
      {(session || opsDocSnapshot) && (
        <div
          data-testid="ddr-context-strip"
          className="card-rs p-3"
          style={{
            background: "rgba(255,179,0,0.06)",
            borderColor: "var(--rs-border)",
            borderLeft: "2px solid var(--rs-amber)",
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="w-3.5 h-3.5" style={{ color: "var(--rs-amber)" }} />
            <span className="text-[10px] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--rs-amber)" }}>
              Engineering Context · {CONTEXT_VERSION}
              {opsDocSnapshot?.frozen ? " · Frozen" : " · Pending freeze"}
            </span>
          </div>
          <div className="text-[11px] text-[var(--rs-text-2)] grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5">
            {opsDocSnapshot ? (
              <>
                <span><b>Operator:</b> {opsDocSnapshot.project.operator || "—"}</span>
                <span><b>Well:</b>     {opsDocSnapshot.well.name || "—"}</span>
                <span><b>Rig:</b>      {opsDocSnapshot.rig.name || "—"}</span>
                <span><b>Section:</b>  {opsDocSnapshot.well.holeSection || "—"}</span>
                <span><b>Depth MD:</b> {opsDocSnapshot.well.currentMdFt || "—"}</span>
                <span><b>Captured:</b> {opsDocSnapshot.capturedAt ? new Date(opsDocSnapshot.capturedAt).toLocaleString() : "—"}</span>
              </>
            ) : (
              <span className="col-span-3 text-[var(--rs-text-3)]">
                Live preview — context will be frozen on first save.
              </span>
            )}
          </div>
        </div>
      )}


      {/* Review checklist */}
      <div
        data-testid="ddr-review-checklist"
        className="card-rs p-3"
        style={{
          background: issues.length ? "rgba(244,67,54,0.06)" : "rgba(76,175,80,0.06)",
          borderColor: issues.length ? "#C62828" : "rgba(76,175,80,0.5)",
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          {issues.length ? (
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: "var(--rs-danger)" }} />
          ) : (
            <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#4CAF50" }} />
          )}
          <span className="text-[12px] font-semibold" style={{ color: issues.length ? "var(--rs-danger)" : "#86EFAC" }}>
            {issues.length ? `Review checklist — ${issues.length} item(s) need attention` : "Review checklist clean — all critical fields entered"}
          </span>
        </div>
        {issues.length > 0 && (
          <ul className="text-[11px] text-[var(--rs-text-2)] list-disc pl-4 leading-snug" data-testid="ddr-review-issues">
            {issues.map((i, idx) => (
              <li key={idx}>{i}</li>
            ))}
          </ul>
        )}
      </div>

      {/* ===== Form sections ===== */}
      <div className="grid grid-cols-1 gap-3">
        {/* 1 — Header */}
        <Section id="header" badge="01" title="Report Header" open={open.header} onToggle={() => toggle("header")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Report Date"><TextInput type="date" value={state.header.reportDate} onChange={(v) => update("header", "reportDate", v)} testId="ddr-h-reportDate" /></Field>
            <Field label="Report Number"><TextInput value={state.header.reportNumber} onChange={(v) => update("header", "reportNumber", v)} placeholder="e.g. 0234" testId="ddr-h-reportNumber" /></Field>
            <Field label="Rig Name"><TextInput value={state.header.rigName} onChange={(v) => update("header", "rigName", v)} placeholder="e.g. Sentinel I" testId="ddr-h-rigName" /></Field>
            <Field label="Contractor"><TextInput value={state.header.contractor} onChange={(v) => update("header", "contractor", v)} testId="ddr-h-contractor" /></Field>
            <Field label="Operator / Client"><TextInput value={state.header.operator} onChange={(v) => update("header", "operator", v)} testId="ddr-h-operator" /></Field>
            <Field label="Well Name"><TextInput value={state.header.wellName} onChange={(v) => update("header", "wellName", v)} testId="ddr-h-wellName" /></Field>
            <Field label="Field / Block"><TextInput value={state.header.field} onChange={(v) => update("header", "field", v)} testId="ddr-h-field" /></Field>
            <Field label="Location"><TextInput value={state.header.location} onChange={(v) => update("header", "location", v)} testId="ddr-h-location" /></Field>
            <Field label="Country / Region"><TextInput value={state.header.country} onChange={(v) => update("header", "country", v)} testId="ddr-h-country" /></Field>
            <Field label="Prepared By"><TextInput value={state.header.preparedBy} onChange={(v) => update("header", "preparedBy", v)} testId="ddr-h-preparedBy" /></Field>
            <Field label="Position"><TextInput value={state.header.position} onChange={(v) => update("header", "position", v)} placeholder="Toolpusher, Driller, Company Rep…" testId="ddr-h-position" /></Field>
            <Field label="Tour / Shift"><TextInput value={state.header.tour} onChange={(v) => update("header", "tour", v)} placeholder="Day / Night / 06:00–18:00" testId="ddr-h-tour" /></Field>
            <Field label="Period Start"><TextInput type="time" value={state.header.periodStart} onChange={(v) => update("header", "periodStart", v)} testId="ddr-h-periodStart" /></Field>
            <Field label="Period End"><TextInput type="time" value={state.header.periodEnd} onChange={(v) => update("header", "periodEnd", v)} testId="ddr-h-periodEnd" /></Field>
            <Field label="Time Zone"><TextInput value={state.header.timeZone} onChange={(v) => update("header", "timeZone", v)} placeholder="UTC / Local / GMT+3 …" testId="ddr-h-timeZone" /></Field>
            <Field label="Unit System">
              <Select value={state.header.unitSystem} onChange={(v) => update("header", "unitSystem", v)} options={["imperial", "metric"]} testId="ddr-h-unitSystem" />
            </Field>
          </div>
        </Section>

        {/* 2 — Well Data */}
        <Section id="well" badge="02" title="Well Data" open={open.well} onToggle={() => toggle("well")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Well Type"><TextInput value={state.well.wellType} onChange={(v) => update("well", "wellType", v)} placeholder="Exploration, Development, Sidetrack…" testId="ddr-w-wellType" /></Field>
            <Field label="Well Status"><TextInput value={state.well.wellStatus} onChange={(v) => update("well", "wellStatus", v)} testId="ddr-w-wellStatus" /></Field>
            <Field label="Hole Section"><TextInput value={state.well.holeSection} onChange={(v) => update("well", "holeSection", v)} placeholder='12 1/4" / 8 1/2" …' testId="ddr-w-holeSection" /></Field>
            <Field label="Hole Size"><TextInput value={state.well.holeSize} onChange={(v) => update("well", "holeSize", v)} testId="ddr-w-holeSize" /></Field>
            <Field label="Current MD"><TextInput type="number" value={state.well.currentMd} onChange={(v) => update("well", "currentMd", v)} testId="ddr-w-currentMd" /></Field>
            <Field label="Current TVD"><TextInput type="number" value={state.well.currentTvd} onChange={(v) => update("well", "currentTvd", v)} testId="ddr-w-currentTvd" /></Field>
            <Field label="Previous Depth"><TextInput type="number" value={state.well.previousDepth} onChange={(v) => update("well", "previousDepth", v)} testId="ddr-w-previousDepth" /></Field>
            <Field label="Footage Drilled Today"><TextInput type="number" value={state.well.footageDrilled} onChange={(v) => update("well", "footageDrilled", v)} testId="ddr-w-footageDrilled" /></Field>
            <Field label="Water Depth (offshore)"><TextInput type="number" value={state.well.waterDepth} onChange={(v) => update("well", "waterDepth", v)} testId="ddr-w-waterDepth" /></Field>
            <Field label="RKB / Rig Floor Elevation"><TextInput type="number" value={state.well.rkbElevation} onChange={(v) => update("well", "rkbElevation", v)} testId="ddr-w-rkbElevation" /></Field>
            <Field label="Mudline / Seabed Reference"><TextInput value={state.well.mudlineRef} onChange={(v) => update("well", "mudlineRef", v)} testId="ddr-w-mudlineRef" /></Field>
            <Field label="Last Casing Size"><TextInput value={state.well.casingSize} onChange={(v) => update("well", "casingSize", v)} testId="ddr-w-casingSize" /></Field>
            <Field label="Shoe Depth MD"><TextInput type="number" value={state.well.shoeMd} onChange={(v) => update("well", "shoeMd", v)} testId="ddr-w-shoeMd" /></Field>
            <Field label="Shoe Depth TVD"><TextInput type="number" value={state.well.shoeTvd} onChange={(v) => update("well", "shoeTvd", v)} testId="ddr-w-shoeTvd" /></Field>
            <Field label="Formation"><TextInput value={state.well.formation} onChange={(v) => update("well", "formation", v)} testId="ddr-w-formation" /></Field>
            <Field label="Target Depth"><TextInput value={state.well.targetDepth} onChange={(v) => update("well", "targetDepth", v)} testId="ddr-w-targetDepth" /></Field>
            <Field label="Planned Next Casing Point"><TextInput value={state.well.nextCasingPoint} onChange={(v) => update("well", "nextCasingPoint", v)} testId="ddr-w-nextCasingPoint" /></Field>
          </div>
        </Section>

        {/* 3 — Operations Summary */}
        <Section id="ops" badge="03" title="Operations Summary" open={open.ops} onToggle={() => toggle("ops")}>
          <Field label="Main Operation Type"><Select value={state.ops.mainOperation} onChange={(v) => update("ops", "mainOperation", v)} options={OP_TYPES} testId="ddr-o-mainOperation" /></Field>
          <Field label="Rough Operations Notes" helper="Write what happened in simple words. RigSentinel will clean the wording."><TextArea value={state.ops.notes} onChange={(v) => update("ops", "notes", v)} rows={5} placeholder="Drilled 12 1/4 hole from 8450 ft to 8720 ft. Pump 1 issue, changed valve insert…" testId="ddr-o-notes" /></Field>
          <QuickPhrases onPick={(p) => appendPhrase("ops", "notes", p)} testId="ddr-o-quick-phrases" />
          <Field label="Main Accomplishments"><TextArea value={state.ops.accomplishments} onChange={(v) => update("ops", "accomplishments", v)} testId="ddr-o-accomplishments" /></Field>
          <Field label="Problems Encountered"><TextArea value={state.ops.problems} onChange={(v) => update("ops", "problems", v)} testId="ddr-o-problems" /></Field>
          <Field label="Corrective Actions"><TextArea value={state.ops.correctiveActions} onChange={(v) => update("ops", "correctiveActions", v)} testId="ddr-o-correctiveActions" /></Field>
          <Field label="Current Status"><TextArea value={state.ops.currentStatus} onChange={(v) => update("ops", "currentStatus", v)} rows={2} testId="ddr-o-currentStatus" /></Field>
          <Field label="Plan for Next Tour / Next 24 Hours"><TextArea value={state.ops.nextPlan} onChange={(v) => update("ops", "nextPlan", v)} testId="ddr-o-nextPlan" /></Field>
        </Section>

        {/* 4 — 24-Hour Activity Breakdown */}
        <Section id="activities" badge="04" title="24-Hour Activity Breakdown" open={open.activities} onToggle={() => toggle("activities")}>
          <div className="flex flex-col gap-3" data-testid="ddr-activities-list">
            {state.activities.length === 0 && (
              <div className="text-[11px] text-[var(--rs-text-3)]">No activities entered. Add the first row below.</div>
            )}
            {state.activities.map((a, idx) => (
              <div key={idx} className="card-rs p-3" data-testid={`ddr-activity-row-${idx}`} style={{ background: "rgba(22,24,29,0.4)" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-rs text-[10px] tracking-[0.18em] uppercase text-[var(--rs-amber)]">Row {idx + 1}</span>
                  <button type="button" onClick={() => deleteActivity(idx)} className="text-[10px] inline-flex items-center gap-1 text-[var(--rs-danger)]" data-testid={`ddr-activity-delete-${idx}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Field label="Start"><TextInput type="time" value={a.start} onChange={(v) => updateActivity(idx, "start", v)} testId={`ddr-activity-start-${idx}`} /></Field>
                  <Field label="End"><TextInput type="time" value={a.end} onChange={(v) => updateActivity(idx, "end", v)} testId={`ddr-activity-end-${idx}`} /></Field>
                  <Field label="Duration (h)"><TextInput type="number" value={a.duration} onChange={(v) => updateActivity(idx, "duration", v)} testId={`ddr-activity-duration-${idx}`} /></Field>
                  <Field label="Operation / Activity"><TextInput value={a.activityType} onChange={(v) => updateActivity(idx, "activityType", v)} placeholder="Drilling, Tripping, Standby…" testId={`ddr-activity-type-${idx}`} /></Field>
                  <Field label="Depth From"><TextInput type="number" value={a.depthFrom} onChange={(v) => updateActivity(idx, "depthFrom", v)} testId={`ddr-activity-depthFrom-${idx}`} /></Field>
                  <Field label="Depth To"><TextInput type="number" value={a.depthTo} onChange={(v) => updateActivity(idx, "depthTo", v)} testId={`ddr-activity-depthTo-${idx}`} /></Field>
                  <Field label="Responsible Dept."><TextInput value={a.responsible} onChange={(v) => updateActivity(idx, "responsible", v)} testId={`ddr-activity-responsible-${idx}`} /></Field>
                  <div className="flex items-end">
                    <Checkbox checked={a.npt} onChange={(v) => updateActivity(idx, "npt", v)} label="NPT" testId={`ddr-activity-npt-${idx}`} />
                  </div>
                </div>
                <div className="mt-2">
                  <Field label="Detailed Activity Notes"><TextArea value={a.notes} onChange={(v) => updateActivity(idx, "notes", v)} rows={2} testId={`ddr-activity-notes-${idx}`} /></Field>
                </div>
                <div className="mt-2">
                  <Field label="Comments"><TextArea value={a.comments} onChange={(v) => updateActivity(idx, "comments", v)} rows={2} testId={`ddr-activity-comments-${idx}`} /></Field>
                </div>
              </div>
            ))}
            <button type="button" onClick={addActivity} data-testid="ddr-activity-add" className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5 self-start">
              <Plus className="w-3.5 h-3.5" /> Add Activity Row
            </button>
            <div className="text-[12px] font-semibold flex flex-wrap gap-x-4 gap-y-1" data-testid="ddr-activity-totals">
              <span>Total Hours: <span className="text-[var(--rs-amber)]">{sums.total} h</span></span>
              <span>NPT: <span className="text-[var(--rs-amber)]">{sums.npt} h</span></span>
              <span>Standby: <span className="text-[var(--rs-amber)]">{sums.standby} h</span></span>
              {sums.total > 0 && Math.abs(sums.total - 24) > 0.05 && (
                <span data-testid="ddr-activity-24h-warning" className="text-[var(--rs-danger)]">
                  ⚠ Activity total ≠ 24 h
                </span>
              )}
            </div>
          </div>
        </Section>

        {/* 5 — Drilling Parameters */}
        <Section id="params" badge="05" title="Drilling Parameters" open={open.params} onToggle={() => toggle("params")}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              ["wob", "Weight on Bit"],
              ["rotaryRpm", "Rotary RPM"],
              ["tdRpm", "Top Drive RPM"],
              ["torque", "Torque"],
              ["pumpPressure", "Pump Pressure"],
              ["flowRate", "Flow Rate"],
              ["spm", "SPM"],
              ["pumpOutput", "Pump Output"],
              ["hookload", "Hookload"],
              ["ropAvg", "ROP Average"],
              ["ropInst", "ROP Instantaneous"],
              ["bitDepth", "Bit Depth"],
              ["holeDepth", "Hole Depth"],
              ["spp", "Standpipe Pressure"],
              ["ecd", "ECD"],
              ["annVel", "Annular Velocity"],
            ].map(([k, label]) => (
              <Field key={k} label={label}><TextInput type="number" value={state.params[k]} onChange={(v) => update("params", k, v)} testId={`ddr-p-${k}`} /></Field>
            ))}
          </div>
        </Section>

        {/* 6 — Mud / Drilling Fluid */}
        <Section id="mud" badge="06" title="Mud / Drilling Fluid" open={open.mud} onToggle={() => toggle("mud")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Mud Type"><TextInput value={state.mud.mudType} onChange={(v) => update("mud", "mudType", v)} placeholder="WBM / OBM / SBM" testId="ddr-m-mudType" /></Field>
            <Field label="Mud Weight"><TextInput type="number" value={state.mud.mudWeight} onChange={(v) => update("mud", "mudWeight", v)} testId="ddr-m-mudWeight" /></Field>
            <Field label="Funnel Viscosity"><TextInput type="number" value={state.mud.funnelVisc} onChange={(v) => update("mud", "funnelVisc", v)} testId="ddr-m-funnelVisc" /></Field>
            <Field label="Plastic Viscosity (PV)"><TextInput type="number" value={state.mud.pv} onChange={(v) => update("mud", "pv", v)} testId="ddr-m-pv" /></Field>
            <Field label="Yield Point (YP)"><TextInput type="number" value={state.mud.yp} onChange={(v) => update("mud", "yp", v)} testId="ddr-m-yp" /></Field>
            <Field label="Gel Strengths (10s/10min/30min)"><TextInput value={state.mud.gels} onChange={(v) => update("mud", "gels", v)} testId="ddr-m-gels" /></Field>
            <Field label="pH"><TextInput type="number" value={state.mud.ph} onChange={(v) => update("mud", "ph", v)} testId="ddr-m-ph" /></Field>
            <Field label="Chlorides"><TextInput type="number" value={state.mud.chlorides} onChange={(v) => update("mud", "chlorides", v)} testId="ddr-m-chlorides" /></Field>
            <Field label="Sand Content (%)"><TextInput type="number" value={state.mud.sand} onChange={(v) => update("mud", "sand", v)} testId="ddr-m-sand" /></Field>
            <Field label="Solids (%)"><TextInput type="number" value={state.mud.solids} onChange={(v) => update("mud", "solids", v)} testId="ddr-m-solids" /></Field>
            <Field label="Oil/Water Ratio (OBM)"><TextInput value={state.mud.owr} onChange={(v) => update("mud", "owr", v)} testId="ddr-m-owr" /></Field>
            <Field label="Fluid Loss"><TextInput type="number" value={state.mud.fluidLoss} onChange={(v) => update("mud", "fluidLoss", v)} testId="ddr-m-fluidLoss" /></Field>
            <Field label="MBT"><TextInput type="number" value={state.mud.mbt} onChange={(v) => update("mud", "mbt", v)} testId="ddr-m-mbt" /></Field>
            <Field label="Mud Temperature"><TextInput type="number" value={state.mud.mudTemp} onChange={(v) => update("mud", "mudTemp", v)} testId="ddr-m-mudTemp" /></Field>
            <Field label="Active Pit Volume"><TextInput type="number" value={state.mud.activePit} onChange={(v) => update("mud", "activePit", v)} testId="ddr-m-activePit" /></Field>
            <Field label="Reserve Pit Volume"><TextInput type="number" value={state.mud.reservePit} onChange={(v) => update("mud", "reservePit", v)} testId="ddr-m-reservePit" /></Field>
            <Field label="Gains / Losses"><TextInput value={state.mud.gainsLosses} onChange={(v) => update("mud", "gainsLosses", v)} testId="ddr-m-gainsLosses" /></Field>
          </div>
          <Field label="Treatments Added"><TextArea value={state.mud.treatments} onChange={(v) => update("mud", "treatments", v)} rows={2} testId="ddr-m-treatments" /></Field>
          <Field label="Mud Engineer Notes"><TextArea value={state.mud.notes} onChange={(v) => update("mud", "notes", v)} rows={2} testId="ddr-m-notes" /></Field>
        </Section>

        {/* 7 — Bit / BHA */}
        <Section id="bha" badge="07" title="Bit / BHA Summary" open={open.bha} onToggle={() => toggle("bha")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Bit Number"><TextInput value={state.bha.bitNumber} onChange={(v) => update("bha", "bitNumber", v)} testId="ddr-b-bitNumber" /></Field>
            <Field label="Bit Size"><TextInput value={state.bha.bitSize} onChange={(v) => update("bha", "bitSize", v)} testId="ddr-b-bitSize" /></Field>
            <Field label="Bit Type"><TextInput value={state.bha.bitType} onChange={(v) => update("bha", "bitType", v)} placeholder="PDC / Tricone…" testId="ddr-b-bitType" /></Field>
            <Field label="Manufacturer"><TextInput value={state.bha.manufacturer} onChange={(v) => update("bha", "manufacturer", v)} testId="ddr-b-manufacturer" /></Field>
            <Field label="IADC Code"><TextInput value={state.bha.iadcCode} onChange={(v) => update("bha", "iadcCode", v)} testId="ddr-b-iadcCode" /></Field>
            <Field label="Jets / Nozzles"><TextInput value={state.bha.jets} onChange={(v) => update("bha", "jets", v)} testId="ddr-b-jets" /></Field>
            <Field label="TFA"><TextInput value={state.bha.tfa} onChange={(v) => update("bha", "tfa", v)} testId="ddr-b-tfa" /></Field>
            <Field label="Motor / RSS Details"><TextInput value={state.bha.motorRss} onChange={(v) => update("bha", "motorRss", v)} testId="ddr-b-motorRss" /></Field>
            <Field label="Stabilizers"><TextInput value={state.bha.stabilizers} onChange={(v) => update("bha", "stabilizers", v)} testId="ddr-b-stabilizers" /></Field>
            <Field label="MWD / LWD Tools"><TextInput value={state.bha.mwdLwd} onChange={(v) => update("bha", "mwdLwd", v)} testId="ddr-b-mwdLwd" /></Field>
            <Field label="Bit Hours"><TextInput type="number" value={state.bha.bitHours} onChange={(v) => update("bha", "bitHours", v)} testId="ddr-b-bitHours" /></Field>
            <Field label="Drilled Interval"><TextInput value={state.bha.drilledInterval} onChange={(v) => update("bha", "drilledInterval", v)} testId="ddr-b-drilledInterval" /></Field>
            <Field label="Average ROP"><TextInput value={state.bha.avgRop} onChange={(v) => update("bha", "avgRop", v)} testId="ddr-b-avgRop" /></Field>
            <Field label="Dull Grade (if pulled)"><TextInput value={state.bha.dullGrade} onChange={(v) => update("bha", "dullGrade", v)} testId="ddr-b-dullGrade" /></Field>
          </div>
          <Field label="BHA Description"><TextArea value={state.bha.bhaDescription} onChange={(v) => update("bha", "bhaDescription", v)} rows={3} testId="ddr-b-bhaDescription" /></Field>
          <Field label="BHA Notes"><TextArea value={state.bha.notes} onChange={(v) => update("bha", "notes", v)} rows={2} testId="ddr-b-notes" /></Field>
        </Section>

        {/* 8 — Pumps / Surface Equipment */}
        <Section id="equipment" badge="08" title="Pumps &amp; Surface Equipment" open={open.equipment} onToggle={() => toggle("equipment")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Pump 1 Status"><TextInput value={state.equipment.pump1} onChange={(v) => update("equipment", "pump1", v)} testId="ddr-e-pump1" /></Field>
            <Field label="Pump 2 Status"><TextInput value={state.equipment.pump2} onChange={(v) => update("equipment", "pump2", v)} testId="ddr-e-pump2" /></Field>
            <Field label="Pump 3 Status"><TextInput value={state.equipment.pump3} onChange={(v) => update("equipment", "pump3", v)} testId="ddr-e-pump3" /></Field>
            <Field label="Liner Size"><TextInput value={state.equipment.linerSize} onChange={(v) => update("equipment", "linerSize", v)} testId="ddr-e-linerSize" /></Field>
            <Field label="Stroke Length"><TextInput value={state.equipment.strokeLength} onChange={(v) => update("equipment", "strokeLength", v)} testId="ddr-e-strokeLength" /></Field>
            <Field label="SPM"><TextInput type="number" value={state.equipment.spm} onChange={(v) => update("equipment", "spm", v)} testId="ddr-e-spm" /></Field>
            <Field label="Pump Output"><TextInput type="number" value={state.equipment.pumpOutput} onChange={(v) => update("equipment", "pumpOutput", v)} testId="ddr-e-pumpOutput" /></Field>
          </div>
          <Field label="Surface Equipment Issues"><TextArea value={state.equipment.surfaceIssues} onChange={(v) => update("equipment", "surfaceIssues", v)} rows={2} testId="ddr-e-surfaceIssues" /></Field>
          <Field label="Repairs Performed"><TextArea value={state.equipment.repairs} onChange={(v) => update("equipment", "repairs", v)} rows={2} testId="ddr-e-repairs" /></Field>
          <Field label="Equipment Notes"><TextArea value={state.equipment.notes} onChange={(v) => update("equipment", "notes", v)} rows={2} testId="ddr-e-notes" /></Field>
        </Section>

        {/* 9 — BOP / Well Control */}
        <Section id="bop" badge="09" title="BOP / Well Control / Safety Critical Equipment" open={open.bop} onToggle={() => toggle("bop")}>
          <div className="card-rs p-2.5" style={{ background: "rgba(244,67,54,0.06)", borderColor: "#C62828" }}>
            <p className="text-[11px] text-[#FCA5A5] leading-snug">
              Recorded status only. Final verification must be performed under approved procedures by competent personnel.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="BOP Status"><TextInput value={state.bop.bopStatus} onChange={(v) => update("bop", "bopStatus", v)} testId="ddr-bop-bopStatus" /></Field>
            <Field label="Choke Manifold"><TextInput value={state.bop.chokeManifold} onChange={(v) => update("bop", "chokeManifold", v)} testId="ddr-bop-chokeManifold" /></Field>
            <Field label="Accumulator Status"><TextInput value={state.bop.accumulator} onChange={(v) => update("bop", "accumulator", v)} testId="ddr-bop-accumulator" /></Field>
            <Field label="Trip Tank Status"><TextInput value={state.bop.tripTank} onChange={(v) => update("bop", "tripTank", v)} testId="ddr-bop-tripTank" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Checkbox checked={state.bop.bopTest} onChange={(v) => update("bop", "bopTest", v)} label="BOP Test Performed" testId="ddr-bop-bopTest" />
            <Checkbox checked={state.bop.bopDrill} onChange={(v) => update("bop", "bopDrill", v)} label="BOP Drill Performed" testId="ddr-bop-bopDrill" />
            <Checkbox checked={state.bop.flowCheck} onChange={(v) => update("bop", "flowCheck", v)} label="Flow Check Performed" testId="ddr-bop-flowCheck" />
            <Checkbox checked={state.bop.shutIn} onChange={(v) => update("bop", "shutIn", v)} label="Well Shut In" testId="ddr-bop-shutIn" />
            <Checkbox checked={state.bop.kickObserved} onChange={(v) => update("bop", "kickObserved", v)} label="Kick / Influx Observed" testId="ddr-bop-kickObserved" />
          </div>
          <Field label="Well Control Notes"><TextArea value={state.bop.notes} onChange={(v) => update("bop", "notes", v)} rows={2} testId="ddr-bop-notes" /></Field>
          <Field label="Safety Critical Equipment Issues"><TextArea value={state.bop.safetyEqIssues} onChange={(v) => update("bop", "safetyEqIssues", v)} rows={2} testId="ddr-bop-safetyEqIssues" /></Field>
        </Section>

        {/* 10 — HSE */}
        <Section id="hse" badge="10" title="HSE / Safety" open={open.hse} onToggle={() => toggle("hse")}>
          <Field label="Incidents" helper="Avoid blame language. Describe facts only."><TextArea value={state.hse.incidents} onChange={(v) => update("hse", "incidents", v)} rows={2} testId="ddr-hse-incidents" /></Field>
          <Field label="Near Misses"><TextArea value={state.hse.nearMisses} onChange={(v) => update("hse", "nearMisses", v)} rows={2} testId="ddr-hse-nearMisses" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="STOP Cards / Observations"><TextInput value={state.hse.stopCards} onChange={(v) => update("hse", "stopCards", v)} testId="ddr-hse-stopCards" /></Field>
            <Field label="Toolbox Talks Completed"><TextInput value={state.hse.toolboxTalks} onChange={(v) => update("hse", "toolboxTalks", v)} testId="ddr-hse-toolboxTalks" /></Field>
            <Field label="Safety Meetings"><TextInput value={state.hse.safetyMeetings} onChange={(v) => update("hse", "safetyMeetings", v)} testId="ddr-hse-safetyMeetings" /></Field>
            <Field label="Environmental Spills / Releases"><TextInput value={state.hse.spill} onChange={(v) => update("hse", "spill", v)} testId="ddr-hse-spill" /></Field>
            <Field label="Dropped-Object Concerns"><TextInput value={state.hse.dropped} onChange={(v) => update("hse", "dropped", v)} testId="ddr-hse-dropped" /></Field>
            <Field label="PPE Concerns"><TextInput value={state.hse.ppe} onChange={(v) => update("hse", "ppe", v)} testId="ddr-hse-ppe" /></Field>
          </div>
          <Field label="Permit-to-Work Concerns"><TextArea value={state.hse.ptw} onChange={(v) => update("hse", "ptw", v)} rows={2} testId="ddr-hse-ptw" /></Field>
          <Field label="SIMOPS Concerns"><TextArea value={state.hse.simops} onChange={(v) => update("hse", "simops", v)} rows={2} testId="ddr-hse-simops" /></Field>
          <Field label="Weather Concerns"><TextArea value={state.hse.weatherConcerns} onChange={(v) => update("hse", "weatherConcerns", v)} rows={2} testId="ddr-hse-weatherConcerns" /></Field>
          <Field label="HSE Notes"><TextArea value={state.hse.notes} onChange={(v) => update("hse", "notes", v)} rows={2} testId="ddr-hse-notes" /></Field>
        </Section>

        {/* 11 — POB / Personnel */}
        <Section id="pob" badge="11" title="Personnel on Board / Crew" open={open.pob} onToggle={() => toggle("pob")}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Operator"><TextInput type="number" value={state.pob.operator} onChange={(v) => update("pob", "operator", v)} testId="ddr-pob-operator" /></Field>
            <Field label="Contractor"><TextInput type="number" value={state.pob.contractor} onChange={(v) => update("pob", "contractor", v)} testId="ddr-pob-contractor" /></Field>
            <Field label="Service Companies"><TextInput type="number" value={state.pob.service} onChange={(v) => update("pob", "service", v)} testId="ddr-pob-service" /></Field>
            <Field label="Catering / Marine / Logistics"><TextInput type="number" value={state.pob.support} onChange={(v) => update("pob", "support", v)} testId="ddr-pob-support" /></Field>
            <Field label="Visitors"><TextInput type="number" value={state.pob.visitors} onChange={(v) => update("pob", "visitors", v)} testId="ddr-pob-visitors" /></Field>
            <Field label="Total POB"><TextInput type="number" value={state.pob.totalPob} onChange={(v) => update("pob", "totalPob", v)} testId="ddr-pob-totalPob" /></Field>
          </div>
          <Field label="Crew Changes"><TextArea value={state.pob.crewChanges} onChange={(v) => update("pob", "crewChanges", v)} rows={2} testId="ddr-pob-crewChanges" /></Field>
          <Field label="Personnel Notes"><TextArea value={state.pob.notes} onChange={(v) => update("pob", "notes", v)} rows={2} testId="ddr-pob-notes" /></Field>
        </Section>

        {/* 12 — Logistics / Materials */}
        <Section id="logistics" badge="12" title="Logistics / Materials" open={open.logistics} onToggle={() => toggle("logistics")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Fuel Received"><TextInput value={state.logistics.fuelReceived} onChange={(v) => update("logistics", "fuelReceived", v)} testId="ddr-l-fuelReceived" /></Field>
            <Field label="Fuel Consumed"><TextInput value={state.logistics.fuelConsumed} onChange={(v) => update("logistics", "fuelConsumed", v)} testId="ddr-l-fuelConsumed" /></Field>
            <Field label="Bulk Materials Received"><TextInput value={state.logistics.bulkReceived} onChange={(v) => update("logistics", "bulkReceived", v)} testId="ddr-l-bulkReceived" /></Field>
            <Field label="Cement"><TextInput value={state.logistics.cement} onChange={(v) => update("logistics", "cement", v)} testId="ddr-l-cement" /></Field>
            <Field label="Barite"><TextInput value={state.logistics.barite} onChange={(v) => update("logistics", "barite", v)} testId="ddr-l-barite" /></Field>
            <Field label="Bentonite"><TextInput value={state.logistics.bentonite} onChange={(v) => update("logistics", "bentonite", v)} testId="ddr-l-bentonite" /></Field>
            <Field label="Chemicals"><TextInput value={state.logistics.chemicals} onChange={(v) => update("logistics", "chemicals", v)} testId="ddr-l-chemicals" /></Field>
            <Field label="Water"><TextInput value={state.logistics.water} onChange={(v) => update("logistics", "water", v)} testId="ddr-l-water" /></Field>
            <Field label="Casing / Tubulars Received"><TextInput value={state.logistics.tubularsReceived} onChange={(v) => update("logistics", "tubularsReceived", v)} testId="ddr-l-tubularsReceived" /></Field>
            <Field label="Boats / Helicopters"><TextInput value={state.logistics.boatsHelis} onChange={(v) => update("logistics", "boatsHelis", v)} testId="ddr-l-boatsHelis" /></Field>
          </div>
          <Field label="Logistics Notes"><TextArea value={state.logistics.notes} onChange={(v) => update("logistics", "notes", v)} rows={2} testId="ddr-l-notes" /></Field>
        </Section>

        {/* 13 — Weather / Marine */}
        <Section id="weather" badge="13" title="Weather / Marine Conditions" open={open.weather} onToggle={() => toggle("weather")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Weather"><TextInput value={state.weather.weather} onChange={(v) => update("weather", "weather", v)} testId="ddr-wx-weather" /></Field>
            <Field label="Wind Speed / Direction"><TextInput value={state.weather.wind} onChange={(v) => update("weather", "wind", v)} testId="ddr-wx-wind" /></Field>
            <Field label="Sea State"><TextInput value={state.weather.seaState} onChange={(v) => update("weather", "seaState", v)} testId="ddr-wx-seaState" /></Field>
            <Field label="Swell"><TextInput value={state.weather.swell} onChange={(v) => update("weather", "swell", v)} testId="ddr-wx-swell" /></Field>
            <Field label="Current"><TextInput value={state.weather.current} onChange={(v) => update("weather", "current", v)} testId="ddr-wx-current" /></Field>
            <Field label="Visibility"><TextInput value={state.weather.visibility} onChange={(v) => update("weather", "visibility", v)} testId="ddr-wx-visibility" /></Field>
            <Field label="Temperature"><TextInput value={state.weather.temp} onChange={(v) => update("weather", "temp", v)} testId="ddr-wx-temp" /></Field>
            <Checkbox checked={state.weather.wow} onChange={(v) => update("weather", "wow", v)} label="Waiting on Weather" testId="ddr-wx-wow" />
          </div>
          <Field label="Marine Notes"><TextArea value={state.weather.notes} onChange={(v) => update("weather", "notes", v)} rows={2} testId="ddr-wx-notes" /></Field>
        </Section>

        {/* 14 — NPT / Downtime */}
        <Section id="npt" badge="14" title="NPT / Downtime" open={open.npt} onToggle={() => toggle("npt")}>
          <Checkbox checked={state.npt.occurred} onChange={(v) => update("npt", "occurred", v)} label="NPT Occurred During This Period" testId="ddr-npt-occurred" />
          {state.npt.occurred && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Start Time"><TextInput type="time" value={state.npt.start} onChange={(v) => update("npt", "start", v)} testId="ddr-npt-start" /></Field>
                <Field label="End Time"><TextInput type="time" value={state.npt.end} onChange={(v) => update("npt", "end", v)} testId="ddr-npt-end" /></Field>
                <Field label="Duration (h)"><TextInput type="number" value={state.npt.duration} onChange={(v) => update("npt", "duration", v)} testId="ddr-npt-duration" /></Field>
                <Field label="Category"><TextInput value={state.npt.category} onChange={(v) => update("npt", "category", v)} testId="ddr-npt-category" /></Field>
                <Field label="Equipment / System"><TextInput value={state.npt.equipment} onChange={(v) => update("npt", "equipment", v)} testId="ddr-npt-equipment" /></Field>
                <Field label="Responsible Department"><TextInput value={state.npt.responsible} onChange={(v) => update("npt", "responsible", v)} testId="ddr-npt-responsible" /></Field>
              </div>
              <Field label="Description"><TextArea value={state.npt.description} onChange={(v) => update("npt", "description", v)} rows={2} testId="ddr-npt-description" /></Field>
              <Field label="Cause (if known)" helper="Use neutral wording. If unsure, leave blank — the report will read 'Cause under review.'"><TextArea value={state.npt.cause} onChange={(v) => update("npt", "cause", v)} rows={2} testId="ddr-npt-cause" /></Field>
              <Field label="Action Taken"><TextArea value={state.npt.action} onChange={(v) => update("npt", "action", v)} rows={2} testId="ddr-npt-action" /></Field>
              <Field label="Preventive Action"><TextArea value={state.npt.preventive} onChange={(v) => update("npt", "preventive", v)} rows={2} testId="ddr-npt-preventive" /></Field>
              <Checkbox checked={state.npt.followUp} onChange={(v) => update("npt", "followUp", v)} label="Follow-up Required" testId="ddr-npt-followUp" />
            </>
          )}
        </Section>
      </div>

      {/* ===== Polished Report Preview ===== */}
      <section className="card-rs p-3.5" data-testid="ddr-preview-card">
        <div className="flex items-center justify-between mb-2">
          <div className="label-up text-[var(--rs-amber)]">Polished Report Preview</div>
          <div className="text-[10px] text-[var(--rs-text-3)]">Updates live as you type</div>
        </div>
        <pre
          data-testid="ddr-preview-text"
          className="text-[11px] leading-snug whitespace-pre-wrap text-[var(--rs-text-2)]"
          style={{
            background: "rgba(10,11,14,0.6)",
            padding: 12,
            borderRadius: 6,
            maxHeight: 480,
            overflowY: "auto",
            fontFamily: "JetBrains Mono, ui-monospace, monospace",
          }}
        >
{polished}
        </pre>
      </section>

      {/* ===== Professionally Polished Draft (opt-in, server-side LLM) ===== */}
      <section data-testid="ddr-polish-section" className="flex flex-col gap-2">
        <PolishPanel
          testIdPrefix="ddr"
          reportType="dailyDrillingReport"
          reportTitle={`Daily Drilling Report${state.header?.reportNumber ? ` #${state.header.reportNumber}` : ""}`}
          safetyCritical={false}
          structuredData={state}
          roughNotes={[state.summary?.roughNotes, state.hse?.roughNotes, state.remarks?.roughNotes].filter(Boolean).join("\n\n")}
          deterministicDraft={polished}
          onExportPdf={(polishedText) => {
            try {
              // Re-use existing DDR PDF builder, swapping in the polished body
              // via a one-off override. The original state is NOT mutated.
              exportDdrPdf({ ...state, _polishedOverride: polishedText });
            } catch (_) { /* exporter surfaces its own toast */ }
          }}
          onExportTranslatedPdf={(translatedText) => {
            try {
              exportDdrPdf({ ...state, _polishedOverride: translatedText });
            } catch (_) { /* exporter surfaces its own toast */ }
          }}
        />
      </section>

      {/* Bottom safety reminder */}
      <div className="card-rs p-3" data-testid="ddr-footer-disclaimer" style={{ background: "rgba(22,24,29,0.65)", borderColor: "var(--rs-border)" }}>
        <p className="text-[11px] leading-snug text-[var(--rs-text-3)]">
          <span className="font-semibold" style={{ color: "var(--rs-text-2)" }}>Documentation tool.</span> Output is a draft. Review, correct, and approve under company procedures before submission.
        </p>
      </div>

      {/* RC-1 — Floating Engineering Toolbar (Save · Export · …) */}
      <FloatingActionToolbar
        testId="ddr-fab"
        actions={[
          { id: "save",   icon: Save,     label: "Save",   onClick: onSave, testId: "fab-ddr-save", primary: true },
          { id: "export", icon: FileDown, label: "Export", onClick: onPdf,  testId: "fab-ddr-export" },
        ]}
      />
    </div>
  );
}
